import { useEffect, useState } from 'react';

/**
 * Install banner for the PWA.
 * - Android/Chrome/Edge: catches `beforeinstallprompt` and shows a clean "Installer" button.
 * - iOS Safari: detects iOS and shows a one-time hint explaining the "Partager → Sur l'écran d'accueil" step.
 * - Dismissible. Remembered for 14 days via localStorage.
 */
export const InstallPWABanner = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const dismissedAt = parseInt(localStorage.getItem('pwa-install-dismissed') || '0', 10);
    const fourteenDays = 14 * 24 * 60 * 60 * 1000;
    if (Date.now() - dismissedAt < fourteenDays) return;

    // Already installed?
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (isStandalone) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    // iOS Safari detection (no beforeinstallprompt support)
    const ua = window.navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    if (iOS && isSafari) {
      setIsIOS(true);
      setShow(true);
    }
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
    setShow(false);
  };

  const dismiss = () => {
    localStorage.setItem('pwa-install-dismissed', String(Date.now()));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm bg-foreground text-white shadow-2xl z-[10000] border border-gold/30 animate-in slide-in-from-bottom"
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <img src="/icon-192.png" alt="" className="w-12 h-12 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-heading text-base leading-tight">Installer l'application</p>
            <p className="text-xs text-white/70 mt-1 leading-snug">
              {isIOS ? (
                <>Touchez <span className="inline-block align-middle mx-1"><svg className="w-3.5 h-3.5 inline" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M12 16V4M5 11l7-7 7 7M5 20h14"/></svg></span> puis <strong className="text-gold">« Sur l'écran d'accueil »</strong> pour installer Groupe KEMPF sur votre iPhone.</>
              ) : (
                <>Installez Groupe KEMPF pour un accès rapide depuis votre écran d'accueil — sans navigateur.</>
              )}
            </p>
            <div className="flex gap-2 mt-3">
              {!isIOS && (
                <button onClick={handleInstall} data-testid="pwa-install-btn"
                  className="flex-1 bg-gold text-white px-3 py-2 text-xs uppercase tracking-wider hover:bg-gold-dark transition-colors">
                  Installer
                </button>
              )}
              <button onClick={dismiss} data-testid="pwa-install-dismiss"
                className="px-3 py-2 text-xs uppercase tracking-wider text-white/70 hover:text-white transition-colors">
                {isIOS ? 'Compris' : 'Plus tard'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
